import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Sparkles, Lock, Send, X, Loader2, KeyRound } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "facow_chat_session";

export const Chatbot = () => {
  const { locale, t } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [credits, setCredits] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recharging, setRecharging] = useState(false);
  const [showMaster, setShowMaster] = useState(false);
  const [masterCode, setMasterCode] = useState("");
  const [masterErr, setMasterErr] = useState("");
  const scrollRef = useRef(null);

  // Init session
  useEffect(() => {
    (async () => {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        try {
          const r = await axios.get(`${API}/chat/credits/${cached}`);
          // If cached session still has credits (paid or master-unlocked), reuse it.
          // Otherwise discard and mint a fresh free session so returning visitors
          // are never greeted with "Crédits épuisés" out of the blue.
          if (r.data.credits > 0) {
            setSessionId(cached);
            setCredits(r.data.credits);
            const m = await axios.get(`${API}/chat/messages/${cached}`);
            setMessages(m.data.messages || []);
            return;
          }
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      const r = await axios.post(`${API}/chat/init`, { locale });
      setSessionId(r.data.session_id);
      setCredits(r.data.credits);
      localStorage.setItem(STORAGE_KEY, r.data.session_id);
    })();
  }, []); // eslint-disable-line

  // Poll for credits update if returning from Stripe success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setCredits(r.data.credits);
          setRecharging(false);
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
      } catch (e) { /* ignore */ }
      if (++attempts < 8) setTimeout(poll, 2000);
    };
    setRecharging(true);
    poll();
  }, []);

  // Scrolling rule (per Fabrice's brief):
  // → On NEW user question, scroll so that the user's bubble is anchored
  //   at the top of the viewport. This way the answer flows in BELOW it,
  //   and the user reads naturally from their question downwards.
  // → On initial open / no new user msg, fall back to bottom-pin.
  useEffect(() => {
    const c = scrollRef.current;
    if (!c) return;
    // Find the last user bubble
    const userBubbles = c.querySelectorAll('[data-testid="chat-msg-user"]');
    if (userBubbles.length > 0) {
      const last = userBubbles[userBubbles.length - 1];
      // Anchor it ~12px below the top of the visible scroll area
      c.scrollTop = last.offsetTop - c.offsetTop - 12;
    } else {
      c.scrollTop = c.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || !sessionId || credits <= 0 || loading) return;
    const userMsg = { id: `local-${Date.now()}`, role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat/message`, { session_id: sessionId, message: userMsg.content });
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: r.data.reply }]);
      setCredits(r.data.credits);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) setCredits(0);
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", content: "⚠️ " + (e.response?.data?.detail || "Erreur") }]);
    } finally {
      setLoading(false);
    }
  };

  const recharge = async () => {
    if (!sessionId) return;
    setRecharging(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "recharge_20",
        session_id: sessionId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setRecharging(false);
      alert("Stripe error: " + (e.response?.data?.detail || e.message));
    }
  };

  const submitMaster = async () => {
    if (!sessionId || !masterCode.trim()) return;
    setMasterErr("");
    try {
      const r = await axios.post(`${API}/chat/unlock`, {
        session_id: sessionId,
        code: masterCode.trim(),
      });
      setCredits(r.data.credits);
      setShowMaster(false);
      setMasterCode("");
    } catch (e) {
      setMasterErr(e.response?.data?.detail || (locale === "fr" ? "Code invalide" : "Invalid code"));
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 glass glow-border rounded-full p-4 flex items-center gap-2 group"
          data-testid="chatbot-open-btn"
          aria-label={t("chatbot.open")}
        >
          {credits > 0
            ? <Sparkles size={22} className="text-[#00F0FF]" />
            : <Lock size={22} className="text-[#D4AF37]" />}
          <span className="hidden md:block text-xs font-mono-tech uppercase tracking-widest text-slate-200">
            {t("chatbot.title")}
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[min(96vw,420px)] h-[min(80vh,640px)] glass glow-border rounded-2xl flex flex-col overflow-hidden"
          data-testid="chatbot-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E293B]">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#00F0FF]" />
              <span className="font-display text-sm tracking-[0.2em] text-amber-glow">FACOW360</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="chatbot-close-btn">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
            <div className="text-xs text-slate-500 italic font-mono-tech">{t("chatbot.welcome")}</div>
            {messages.map((m) => (
              <div
                key={m.id}
                className={`max-w-[88%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-slate-100"
                    : "bg-[#0A1128] border border-[#D4AF37]/20 text-slate-200"
                }`}
                data-testid={`chat-msg-${m.role}`}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-slate-400 font-mono-tech">
                <Loader2 size={14} className="animate-spin" /> {t("chatbot.thinking")}
              </div>
            )}
          </div>

          <div className="border-t border-[#1E293B] p-3">
            <div className="text-[10px] uppercase font-mono-tech tracking-[0.25em] text-slate-500 mb-2 text-center" data-testid="chatbot-credits">
              {credits > 0
                ? t("chatbot.paid_left").replace("{n}", credits)
                : t("chatbot.locked")}
            </div>
            {credits <= 0 ? (
              <div className="space-y-2">
                <button
                  onClick={recharge}
                  disabled={recharging}
                  className="btn-gold w-full"
                  data-testid="chatbot-recharge-btn"
                >
                  {recharging ? <Loader2 size={14} className="animate-spin" /> : null}
                  {t("chatbot.recharge")}
                </button>
                {!showMaster ? (
                  <button
                    onClick={() => setShowMaster(true)}
                    className="w-full text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-[#D4AF37] transition flex items-center justify-center gap-1 font-mono-tech"
                    data-testid="chatbot-master-toggle"
                  >
                    <KeyRound size={10} /> {locale === "fr" ? "Accès propriétaire" : "Owner access"}
                  </button>
                ) : (
                  <div className="space-y-2" data-testid="chatbot-master-block">
                    <div className="flex gap-2">
                      <input
                        className="input-tech text-xs"
                        type="password"
                        placeholder={locale === "fr" ? "Code maître" : "Master code"}
                        value={masterCode}
                        onChange={(e) => setMasterCode(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && submitMaster()}
                        data-testid="chatbot-master-input"
                      />
                      <button
                        onClick={submitMaster}
                        disabled={!masterCode.trim()}
                        className="btn-primary !px-3 !py-1 !text-xs"
                        data-testid="chatbot-master-submit"
                      >
                        {locale === "fr" ? "OK" : "OK"}
                      </button>
                    </div>
                    {masterErr && (
                      <p className="text-[10px] text-red-400" data-testid="chatbot-master-error">{masterErr}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="input-tech text-sm"
                  placeholder={t("chatbot.placeholder")}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  disabled={loading}
                  data-testid="chatbot-input"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="btn-primary !px-4"
                  data-testid="chatbot-send-btn"
                  aria-label="send"
                >
                  <Send size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
